import React from 'react';

import type { ConfirmedServiceQuality, QualityRatio, ServiceQualityView } from '../api/integrity';
import { Kpi, KpiRow, Note, Pill, PillTone, Row, Table } from '../../../design-system/ops';
import { UTC_ZONE, formatShortDateTime } from '../time/zonedTime';

/**
 * ADM-13-FR-08 — MC-06's "Ciclos con calidad confirmada por la familia", with
 * the four indicators it is always shown beside (§7: "Ninguno se presenta
 * solo"), its coverage, its causes, and what it excluded.
 *
 * Three things the panel refuses to do, because MC-06 forbids them:
 *  - show the percentage without its counts, or a 0/0 as 0 % (MC06-R14);
 *  - read silence as dissatisfaction (§1) — "sin respuesta" is its own row;
 *  - present a provisional or incomplete month as final (§5).
 */

/** "55 %", "85,7 %" — one decimal only when there is one; the counts beside it. */
export function qualityLabel(r: QualityRatio): string {
  if (r.status === 'NO_RESPONSES') return 'Sin respuestas';
  if (r.status === 'NO_ELIGIBLE_CYCLES' || r.value === null) return 'Sin ciclos elegibles';
  const pct = Math.round(r.value * 1000) / 10;
  const text = Number.isInteger(pct) ? String(pct) : pct.toFixed(1).replace('.', ',');
  return `${text} % (${r.numerator}/${r.denominator})`;
}

const STATUS: Record<ConfirmedServiceQuality['status'], { tone: PillTone; label: string }> = {
  FINAL: { tone: 'ok', label: 'Final' },
  PROVISIONAL: { tone: 'warn', label: 'Provisional' },
  INCOMPLETE: { tone: 'bad', label: 'Incompleto' },
};

export const QUALITY_STATUS_LABEL = (s: ConfirmedServiceQuality['status']) => STATUS[s].label;

const INCIDENTS: Record<ConfirmedServiceQuality['incidents'][number]['incident'], string> = {
  SURVEY_NOT_OPENED: 'Encuesta sin abrir',
  SURVEY_FAILED: 'Encuesta fallida (no llegó a la familia)',
  EVIDENCE_INTEGRATION_FAILURE: 'Fallo de integración de evidencia',
  CANCELLATION_ACTOR_UNRECORDED: 'Cancelación sin responsable registrado',
};

const EVIDENCE_GAPS: Record<
  keyof ConfirmedServiceQuality['breakdown']['completedWithoutEvidence'],
  string
> = {
  MISSING: 'Evidencia faltante',
  INVALID: 'Evidencia inválida',
  PENDING_VALIDATION: 'Pendiente de validación',
  INTEGRATION_FAILURE: 'Fallo de integración',
};

const SHORTFALLS: Record<keyof ConfirmedServiceQuality['breakdown']['notCompleted'], string> = {
  SESSION_NOT_DELIVERED: 'Sesión no impartida',
  CANCELLED_BY_PLATFORM: 'Cancelado por la plataforma',
  CANCELLED_BY_FAMILY: 'La familia terminó el ciclo antes',
  CANCELLED_ACTOR_UNRECORDED: 'Cancelado, responsable sin registrar',
};

const SATISFACTION: Record<keyof ConfirmedServiceQuality['breakdown']['satisfaction'], string> = {
  FAVORABLE: 'Favorable (4 o 5)',
  UNFAVORABLE: 'No favorable (1 a 3)',
  NO_RESPONSE: 'Sin respuesta',
  WINDOW_OPEN: 'Ventana abierta',
  SURVEY_FAILED: 'Encuesta fallida',
  NOT_OPENED: 'Encuesta sin abrir',
};

const EXCLUSIONS: Record<keyof ConfirmedServiceQuality['excluded'], string> = {
  NEVER_CONFIRMED: 'Solicitud nunca confirmada',
  NO_VERIFIABLE_CYCLE: 'Sin ciclo verificable (fuera de cobertura)',
  FAMILY_CANCELLED_BEFORE_START: 'La familia canceló antes de empezar',
  CANCELLATION_ACTOR_UNRECORDED: 'Cancelado antes de empezar, responsable sin registrar',
};

const Counts: React.FC<{ title: string; labels: Record<string, string>; counts: object }> = ({
  title,
  labels,
  counts,
}) => (
  <Table columns="1fr 90px" head={[title, 'Ciclos']}>
    {Object.entries(labels).map(([key, label]) => (
      <Row key={key}>
        <span>{label}</span>
        <span title={`${label}: ${(counts as Record<string, number>)[key] ?? 0}`}>
          {(counts as Record<string, number>)[key] ?? 0}
        </span>
      </Row>
    ))}
  </Table>
);

export const ServiceQualityPanel: React.FC<{
  quality: Exclude<ServiceQualityView, { status: 'INACTIVE' }>;
}> = ({ quality }) => {
  if (quality.status === 'SOURCE_UNAVAILABLE') {
    return (
      <Note tone="bad">
        No se pudieron leer las encuestas de satisfacción del periodo {quality.period}. No se
        muestra el indicador: un fallo de lectura no es una mala valoración.
      </Note>
    );
  }
  const i = quality.indicators;
  const s = STATUS[quality.status];

  return (
    <>
      <p>
        <Pill tone={s.tone}>{s.label}</Pill>{' '}
        <span className="mut">
          Periodo {quality.period} ({quality.reportingTimeZone}) · Corte{' '}
          {formatShortDateTime(quality.cutoff, UTC_ZONE)} · Definición {quality.definition.id} v
          {quality.definition.version} · Revisión {quality.revision}
        </span>
      </p>
      {quality.incidents.length > 0 ? (
        <Note tone="warn">
          {quality.incidents.map((x) => `${INCIDENTS[x.incident]}: ${x.cycles}`).join(' · ')}
        </Note>
      ) : null}
      <KpiRow>
        <Kpi label="Calidad confirmada" value={qualityLabel(i.confirmedQuality)} />
        <Kpi label="Servicio completado" value={qualityLabel(i.serviceCompleted)} />
        <Kpi label="Servicio completo con evidencia" value={qualityLabel(i.serviceWithEvidence)} />
        <Kpi
          label="Satisfacción favorable entre respuestas"
          value={qualityLabel(i.favorableAmongResponses)}
          note="Solo entre quienes respondieron"
        />
        <Kpi label="Cobertura de respuesta" value={qualityLabel(i.responseCoverage)} />
      </KpiRow>
      <Note>
        Sin respuesta no es insatisfacción: el ciclo sigue en el denominador y no recibe nota. La
        calidad del servicio no mide lo que aprendió el alumno, y este indicador no mueve pagos,
        retenciones ni reembolsos.
      </Note>
      <Counts
        title="Servicio completado sin evidencia completa, por causa"
        labels={EVIDENCE_GAPS}
        counts={quality.breakdown.completedWithoutEvidence}
      />
      <Counts
        title="Servicio no completado, por causa"
        labels={SHORTFALLS}
        counts={quality.breakdown.notCompleted}
      />
      <Counts
        title="Valoración de la familia"
        labels={SATISFACTION}
        counts={quality.breakdown.satisfaction}
      />
      <Counts title="Fuera de la población" labels={EXCLUSIONS} counts={quality.excluded} />
      <p className="mut">
        Ciclos en curso (fuera de la cohorte): {quality.inProgress.total}, de ellos{' '}
        {quality.inProgress.affected} afectados por una sesión no impartida o vencida.
      </p>
    </>
  );
};

export default ServiceQualityPanel;
